import axios, { type AxiosResponse } from 'axios';
import { useUserStore } from '@/store/user';
import { useServiceModeStore } from '@/store/service-mode';
import { usePageSettingsStore } from '@/store/page-settings';
import { isTauri } from '@aigatewayx/shared/types/service-mode';
import { ElMessage } from 'element-plus';

/** API response envelope — matches server's unified response format */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  error?: { code: string; message: string };
  meta?: Record<string, unknown>;
  _httpStatus?: number;
}

/**
 * Typed Axios instance.
 *
 * The response interceptor unwraps `AxiosResponse.data` and returns the
 * API envelope directly, so callers receive `ApiResponse<T>` instead of
 * `AxiosResponse<ApiResponse<T>>`.
 */
const request = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
}) as any;

/**
 * In Tauri (desktop app), the frontend runs at tauri://localhost origin.
 * The browser's CORS restrictions block cross-origin requests from the
 * custom tauri:// scheme to http://localhost:3000 even when the server
 * sends Access-Control-Allow-Origin: *.
 *
 * To work around this, we use @tauri-apps/plugin-http's native fetch,
 * which makes requests from the Rust side — completely bypassing CORS.
 *
 * The Rust plugin (tauri-plugin-http) is registered in lib.rs.
 */
let tauriFetch: ((input: URL | Request | string, init?: any) => Promise<Response>) | null = null;

async function loadTauriFetch() {
  if (tauriFetch) return tauriFetch;
  try {
    const { fetch: tauriHttpFetch } = await import('@tauri-apps/plugin-http');
    tauriFetch = tauriHttpFetch;
    return tauriFetch;
  } catch {
    return null;
  }
}

/** Custom axios adapter that delegates to Tauri's native fetch (bypasses CORS) */
function createTauriAdapter() {
  return async (config: any) => {
    const fetchFn = await loadTauriFetch();
    if (!fetchFn) throw new Error('Tauri HTTP plugin not available');

    const base = (config.baseURL || '').replace(/\/+$/, '');
    const path = (config.url || '').startsWith('/') ? config.url : `/${config.url || ''}`;
    const fullPath = `${base}${path}`;

    const method = (config.method || 'get').toUpperCase();
    const headers: Record<string, string> = { ...(config.headers || {}) };

    try {
      const response = await fetchFn(fullPath, {
        method,
        headers,
        body: config.data !== undefined && config.data !== null
          ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data))
          : undefined,
      });

      const responseHeaders: Record<string, string> = {};
      if (typeof response.headers?.forEach === 'function') {
        response.headers.forEach((value: string, key: string) => {
          responseHeaders[key] = value;
        });
      } else if (response.headers?.entries) {
        for (const [key, value] of response.headers.entries()) {
          responseHeaders[key] = value;
        }
      }

      let responseData: any;
      const contentType = response.headers.get?.('content-type') || '';
      if (contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      return {
        data: responseData,
        status: response.status,
        statusText: response.statusText || '',
        headers: responseHeaders,
        config,
      };
    } catch (err: any) {
      console.error('[Tauri HTTP plugin error]', {
        url: fullPath,
        method,
        error: err?.message || err,
        stack: err?.stack,
        name: err?.name,
      });
      throw new Error(`HTTP请求失败: ${err?.message || err}`);
    }
  };
}

request.interceptors.request.use(
  (config: any) => {
    if (isTauri()) {
      try {
        const store = useServiceModeStore();
        if (store.isRemoteMode && !request.defaults.adapter) {
          request.defaults.adapter = createTauriAdapter();
        }
        const apiBase = store.currentApiBase;
        if (apiBase) {
          config.baseURL = `${apiBase.replace(/\/+$/, '')}/api`;
        }
      } catch {}
    }
    const userStore = useUserStore();
    if (userStore.accessToken) {
      config.headers.Authorization = `Bearer ${userStore.accessToken}`;
    }
    try {
      const pageSettings = usePageSettingsStore();
      if (pageSettings.settings.timezone) {
        if (!config.params) config.params = {};
        if (!config.params.timezone) config.params.timezone = pageSettings.settings.timezone;
      }
    } catch {}
    return config;
  },
  (error: any) => Promise.reject(error)
);

request.interceptors.response.use(
  (response: AxiosResponse) => {
    if (isTauri()) {
      try { useServiceModeStore().connectionVerified = true; } catch {}
    }
    const data = response.data;
    if (data && typeof data === 'object') {
      data._httpStatus = response.status;
    }
    if (data && data.success === false) {
      ElMessage.error(data.error?.message || '请求失败');
      return Promise.reject(new Error(data.error?.message));
    }
    return data;
  },
  (error: any) => {
    if (isTauri()) {
      try {
        const store = useServiceModeStore();
        store.connectionVerified = !!error.response;
      } catch {}
    }
    if (error.response?.status === 401) {
      const userStore = useUserStore();
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const redirectQuery: Record<string, string> = { expired: '1' };
      if (currentPath.startsWith('/m/')) {
        redirectQuery.redirect = currentPath;
      }
      userStore.logout(redirectQuery, isMobile ? '/m/login' : '/login');
    } else if (error.response?.status === 403) {
      ElMessage.error('权限不足');
    } else if (error.response?.status === 429) {
      ElMessage.warning('请求过于频繁，请稍后重试');
    } else {
      const msg = error.response?.data?.error?.message || error.message || '网络错误';
      ElMessage.error(msg);
    }
    return Promise.reject(error);
  }
);

export default request;
